import {
  allocateRentCar,
  finalizeRentCarShopping,
  makeRentCarPayment,
  makeRentCarPrebooking,
  searchRentCars,
  updateRentCarPassengers,
} from '../../api/endpoints/rentCar';
import type {
  PaymentForm,
  PaymentResponse,
  RentCarSearchRequest,
  RentCarSearchResponse,
  RentCarVehicle,
} from '../../api/types/rentCar';
import type { Contact, Passenger } from '../../api/types/common';
import type { ShoppingCart } from '../../api/types/shoppingCart';
import { redirectToBankIfNeeded } from '../../components/payment/paymentRedirect';
import { cartIsPaid, type PersistedBookingFlowConfig } from '../../booking-flow/resume';
import { rentCarPersistConfig } from './resume';

/**
 * Common flow (migration guide Bölüm 6.1): Search → Allocate → UpdatePassengers
 * (driver info) → MakePrebooking → FinalizeShopping → MakePayment → Document. Each
 * step's response feeds ShoppingCartId/booking refs into the next step's request —
 * this state object accumulates exactly that. `select` folds "pick a vehicle from the
 * Search results" and the Allocate call into one step, since picking a vehicle is what
 * triggers Allocate.
 */
export interface RentCarFlowState {
  searchRequest?: RentCarSearchRequest;
  searchResult?: RentCarSearchResponse;
  selectedVehicle?: RentCarVehicle;
  shoppingCartId?: string;
  cart?: ShoppingCart | null;
  driver?: { passenger: Passenger; contact: Contact };
  /** Kurumsal bütçe/politika dışı araç kiralama sürücü adımı "Onaya Gönder" ile
   *  tamamlandı; `RentCarFlowPage` bilgi verip rezervasyon listesine döner. Transient. */
  sentForApproval?: boolean;
  payment?: PaymentResponse;
  paymentForm?: PaymentForm;
}

function toShoppingCartId(cart: ShoppingCart | null | undefined): string | undefined {
  return cart?.id ?? undefined;
}

function searchStep(state: RentCarFlowState): Promise<Partial<RentCarFlowState>> {
  if (!state.searchRequest) throw new Error('Arama kriterleri eksik.');
  return searchRentCars(state.searchRequest).then((result) => ({ searchResult: result }));
}

/**
 * CONFIRMED via live swagger (2026-08-27): RentCar/Allocate takes `{ code }` — the
 * selected search-result vehicle's id/code — plus an optional `shoppingCartId`.
 */
function selectStep(state: RentCarFlowState): Promise<Partial<RentCarFlowState>> {
  const vehicle = state.selectedVehicle;
  if (!vehicle) throw new Error('Bir araç seçilmedi.');
  const code = vehicle.id ?? vehicle.code;
  if (!code) throw new Error('Seçilen aracın kimliği bulunamadı.');
  return allocateRentCar({
    code,
    shoppingCartId: state.shoppingCartId,
  }).then((res) => ({
    cart: res.shoppingCart,
    shoppingCartId: toShoppingCartId(res.shoppingCart),
  }));
}

function updatePassengersStep(state: RentCarFlowState): Promise<Partial<RentCarFlowState>> {
  if (!state.shoppingCartId) throw new Error('Sepet bulunamadı — önce araç seçimi/rezervasyona alma tamamlanmalı.');
  const passenger = state.driver?.passenger;
  return updateRentCarPassengers({
    shoppingCartId: state.shoppingCartId,
    passengers: passenger ? [passenger] : [],
    contact: state.driver?.contact,
  }).then((res) => ({ cart: res.shoppingCart }));
}

function prebookingStep(state: RentCarFlowState): Promise<Partial<RentCarFlowState>> {
  if (!state.shoppingCartId) throw new Error('Sepet bulunamadı.');
  return makeRentCarPrebooking({ shoppingCartId: state.shoppingCartId }).then((res) => {
    // `HasError: false` + `ShoppingCart: null` de dönebiliyor — adımı sessizce geçirmek
    // yerine hata fırlatıp akışı bloke ediyoruz.
    if (!res.shoppingCart) throw new Error('Ön rezervasyon oluşturulamadı — sepet boş döndü.');
    return { cart: res.shoppingCart };
  });
}

function finalizeStep(state: RentCarFlowState): Promise<Partial<RentCarFlowState>> {
  if (!state.shoppingCartId) throw new Error('Sepet bulunamadı.');
  return finalizeRentCarShopping({ shoppingCartId: state.shoppingCartId }).then((res) => ({
    cart: res.shoppingCart,
  }));
}

/**
 * installmentId (a Guid) has no documented source endpoint in this module's scope —
 * left unset as a known stretch/TODO; a real integration would fetch it from an
 * installment-options endpoint (out of RentCar's scope) first.
 */
function paymentStep(state: RentCarFlowState): Promise<Partial<RentCarFlowState>> {
  if (!state.shoppingCartId) throw new Error('Sepet bulunamadı.');
  // Yenilenmiş/geri dönülmüş bir akışta ödenmiş sepete ikinci kez ödeme denenmesin.
  if (cartIsPaid(state.cart)) return Promise.reject(new Error('Bu rezervasyon zaten ödenmiş.'));
  const amount = state.cart?.remainingTotal ?? 0;
  return makeRentCarPayment({
    shoppingCartId: state.shoppingCartId,
    amount,
    paymentForm: {
      ...state.paymentForm,
      installmentCount: state.paymentForm?.installmentCount ?? 1,
    },
  }).then((res) => {
    if (redirectToBankIfNeeded(res)) return new Promise<Partial<RentCarFlowState>>(() => {});
    return { payment: res, cart: res.shoppingCart ?? state.cart };
  });
}

function documentStep(): Promise<Partial<RentCarFlowState>> {
  return Promise.resolve({});
}

export const rentCarFlowConfig: PersistedBookingFlowConfig<RentCarFlowState> = {
  id: 'rentCar',
  initialState: {},
  persist: rentCarPersistConfig,
  steps: [
    { id: 'search', label: 'Arama', run: searchStep },
    { id: 'select', label: 'Araç Seçimi', run: selectStep },
    { id: 'driver', label: 'Sürücü Bilgisi', run: updatePassengersStep },
    { id: 'prebooking', label: 'Ön Rezervasyon', run: prebookingStep },
    { id: 'finalize', label: 'Kesinleştirme', run: finalizeStep },
    { id: 'payment', label: 'Ödeme', run: paymentStep },
    { id: 'document', label: 'Belge', run: documentStep },
  ],
};
